import orderBy from 'lodash.orderby';
import tinycolor from 'tinycolor2/tinycolor';

import store from '@/store';

/**
 * Return a deep copy of given object.
 *
 * Object must only contain primitive values (no functions etc.)
 * since it is serialized/deserialized and those would break.
 *
 * A poor man's deep clone: Use only for small/simple objects,
 * otherwise it will be slow.
 *
 */
export function deepCopyObject(obj) {
  return JSON.parse(JSON.stringify(obj));
}

export function capitalize(text) {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function getFilenameFromUrl(url) {
  const filename = url.substring(url.lastIndexOf('/') + 1);
  return filename;
}

export function getMainTVSpotFromBundle(bundle) {
  const spot = bundle.tvspots.filter(
    (tvspot) => tvspot.id === bundle.main_tvspot_id
  )[0];
  return spot;
}

export function urlQueryParamToBoolean(queryParam) {
  return ['true', '1', 'on', 'yes', 'enabled'].includes(
    String(queryParam).toLowerCase()
  );
}

/**
 * Remove one specific query parameter from the current URL without a page reload.
 */
export function removeQueryParamFromCurrentRoute(name) {
  var url = window.location.href;
  var urlParts = url.split('?');

  if (urlParts.length >= 2) {
    var prefix = encodeURIComponent(name) + '=';
    var queryParams = urlParts[1].split(/[&;]/g);

    // Iterate through the query parameters and remove the specified one
    for (var i = queryParams.length; i-- > 0; ) {
      if (queryParams[i].lastIndexOf(prefix, 0) !== -1) {
        queryParams.splice(i, 1);
      }
    }

    // Reconstruct the URL with the modified query parameters
    url =
      urlParts[0] + (queryParams.length > 0 ? '?' + queryParams.join('&') : '');

    // Use replaceState to update the URL without reloading the page
    window.history.replaceState(null, null, url);
  }
}

/**
 * Remove *ALL* query parameters from the current URL without a page reload.
 */
export function removeUrlQueryParamsFromCurrentRoute() {
  // https://stackoverflow.com/a/41061471
  window.history.replaceState(null, null, window.location.pathname);
}

export function copyToClipboard(text, callback = null) {
  var el = document.createElement('textarea');
  el.value = text;
  el.setAttribute('readonly', '');
  el.style.position = 'absolute';
  el.style.left = '-9999px';
  document.body.appendChild(el);
  const selected =
    document.getSelection().rangeCount > 0
      ? document.getSelection().getRangeAt(0)
      : false;
  el.select();
  document.execCommand('copy');
  document.body.removeChild(el);

  if (callback) {
    callback(text);
  }
}

export function getMatchingTVSpotForBundleAndTVDecoder(bundle, tvdecoder) {
  const spot = bundle.tvspots.filter(
    (tvspot) =>
      !!tvspot.tvformat_id && tvspot.tvformat_id === tvdecoder.tvformat_id
  )[0];
  return spot;
}

/**
 * Whether given input is an Array.
 */
export function isArray(input) {
  return Object.prototype.toString.call(input) === '[object Array]';
}

/**
 * Flatten given Array (one level deep).
 *
 * This exists as Array.flat() is not widely used across browsers yet.
 *
 * From: https://developer.mozilla.org/de/docs/Web/JavaScript/Reference/Global_Objects/Array/flat
 */
export function flattenArray(arr) {
  return arr.reduce((acc, val) => acc.concat(val), []);
}

/**
 * Convert an object with ctype fields to a list of ctype strings.
 *
 * Args:
 *   ctypesObj (object): E.g. {radio: false, tv: true, tickers: true}
 *
 *   interpretNoneAsAll (Boolean): Optional. If true and object has no
 *     true fields, return all field strings anyway. This is useful for
 *     filter widgets, where we want 'nothing selected' to be the
 *     default, but interpreted as 'all ctypes selected'.
 *
 * Returns:
 *   Array[String]: E.g. ['radio', 'tv', 'tickers']
 */
export function ctypesObjToArr(ctypesObj, interpretNoneAsAll = true) {
  let ctypesArr = [];
  if (ctypesObj.radio) {
    ctypesArr.push('radio');
  }
  if (ctypesObj.tv) {
    ctypesArr.push('tv');
  }
  if (ctypesObj.tickers) {
    ctypesArr.push('tickers');
  }

  if (ctypesArr.length === 0 && interpretNoneAsAll) {
    ctypesArr.push('radio');
    ctypesArr.push('tv');
    ctypesArr.push('tickers');
  }

  return ctypesArr;
}

export function transformPriceValueToUseNumberOfDecimals(value, num = 2) {
  const isNumber = value !== null && value !== '' && !isNaN(value);
  if (isNumber) {
    value = Number(value).toFixed(num);
  }
  return value;
}

/**
 * Return complimentary color as hex string e.g. '#ffeeff'.
 */
export function hexToComplimentary(hexString) {
  return tinycolor(hexString).complement().toHexString();
}

/**
 * Return whether the given color is considered to be light.
 */
export function isLightColor(hexString) {
  return tinycolor(hexString).isLight();
}

/**
 * Return modified version of given color with specified alpha value.
 *
 * Input is expected to be anything that tinycolor understands, output
 * is a HEXA string like '#ffdcc980'.
 */
export function modifyHexColorAlpha(hexString, alpha) {
  const color = tinycolor(hexString);
  color.setAlpha(alpha);
  return color.toHex8String();
}

export function anyColorToHex(colorString) {
  const color = tinycolor(colorString);
  return color.toHexString();
}

/**
 * Map a value from source range to target range.
 *
 *   Example:
 *     mapRange(0, 100, 10, 20, 50)
 *     >>> 15.0
 *
 *   Based on the rosettacode implementation.
 *
 *   Args:
 *     sourceMin (Number): Lower border of source range
 *     sourceMax (Number): Higher border of source range
 *     targetMin (Number): Lower border of target range
 *     targetMax (Number): Higher border of target range
 *
 *   Returns:
 *     Number: The mapped value
 *
 */
export function mapRange(sourceMin, sourceMax, targetMin, targetMax, value) {
  return (
    targetMin +
    ((value - sourceMin) * (targetMax - targetMin)) / (sourceMax - sourceMin)
  );
}

export function redirectToExternalUrl(vm, url, waitForMs = 300) {
  // Components may have added a question to be confirmed by the user
  // to prevent accidental route changes.
  if (store.state.common.preRouteQuestion) {
    let confirmed = confirm(store.state.common.preRouteQuestion);
    if (!confirmed) {
      return;
    } else {
      store.commit('UNSET_PRE_ROUTE_QUESTION');
    }
  }

  // Note: window.location.replace() would not work with our
  //   local dev setup where we have two servers and the cookie
  //   would not be accepted.
  //
  //   See: https://bytes.com/topic/javascript/answers/90960-window-location-cookies#post304570
  vm.$bus.$emit('alert-info', {
    message: vm.$t('redirect'),
  });
  setTimeout(() => (window.location.href = url), waitForMs);
}

/**
 * Generate a random ID.
 *
 * There's no guarantuee it will be unique, but it is highly unlikely.
 * Mostly useful to generate keys for Vue's rendering or for css selectors.
 *
 * https://learnersbucket.com/examples/javascript/unique-id-generator-in-javascript/
 */
export function generateRandomId() {
  function s4() {
    return Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  }
  // return id of format 'aaaaaaaa'-'aaaa'-'aaaa'-'aaaa'-'aaaaaaaaaaaa'
  return (
    s4() +
    s4() +
    '-' +
    s4() +
    '-' +
    s4() +
    '-' +
    s4() +
    '-' +
    s4() +
    s4() +
    s4()
  );
}

/**
 * Exclude duplicate objects from given array based on their .id.
 *
 * Returns a new filtered array with unique objects.
 *
 * E.g. [{id: 1}, {id: 1}, {id: 2}] -> [{id: 1}, {id: 2}]
 */
export function deduplicateObjectArrayById(objects) {
  const idToObject = {};
  for (const obj of objects) {
    idToObject[obj.id] = obj;
  }
  return Object.values(idToObject);
}

/**
 * Given some videos or TVSpots sort them as we like it here.
 *
 * E.g. for a bundle of videos we'd want this order: 16:9, 9:16, 32:9.
 *
 * Returns new sorted array.
 */
export function sortVideoObjectsByPreferredFormatOrder(
  objectsToSort,
  {widthAttr = 'videoWidth', heightAttr = 'videoHeight'} = {}
) {
  return orderBy(objectsToSort, [
    (obj) => {
      if (!obj[widthAttr] || !obj[heightAttr]) {
        return -1;
      }
      const ratio = obj[widthAttr] / obj[heightAttr];
      return (
        {
          [16 / 9]: 1,
          [9 / 16]: 2,
          [32 / 9]: 3,
        }[ratio] || 5
      );
    },
  ]);
}



// WEBPACK FOOTER //
// ./src/utils.js